/**
 * @vra/schema — Annexure A digitised Daily Trip Sheet, field for field with
 * the paper form, plus the Daily KM Statement roll-up (Annexure 8.4).
 * All kilometre and SOC values are integers (spec §5.3); timings are strings
 * in IST with the device timestamp fixed at capture (§12.3).
 */
export * from "./itms.js";
export * from "./weekly.js";
export const ARTEFACT_DAILY_RETURN = "DAILY_RETURN";
export const LOSS_REASON_CODES = [
    "BREAKDOWN",
    "ACCIDENT",
    "DIVERSION",
    "CANCELLATION",
    "CASUAL_CONTRACT",
];
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;
const ID_RE = /^[A-Za-z0-9._-]+$/;
function isInt(n) {
    return typeof n === "number" && Number.isSafeInteger(n);
}
/**
 * Validates a daily return. Returns a list of problems; empty list = valid.
 * These rules are re-enforced in chaincode — the form is convenience (§6.1).
 */
export function validateDailyReturn(r) {
    const e = [];
    const nonNegInt = (v, name) => {
        if (!isInt(v) || v < 0)
            e.push(`${name} must be a non-negative integer`);
    };
    const soc = (v, name) => {
        if (!isInt(v) || v < 0 || v > 100)
            e.push(`${name} must be an integer 0–100`);
    };
    const id = (v, name) => {
        if (!ID_RE.test(v))
            e.push(`${name} is not a valid identifier: ${JSON.stringify(v)}`);
    };
    if (r.artefactType !== ARTEFACT_DAILY_RETURN)
        e.push("artefactType must be DAILY_RETURN");
    if (!isInt(r.version) || r.version < 1)
        e.push("version must be an integer >= 1");
    id(r.depotId, "depotId");
    id(r.busRegistrationNo, "busRegistrationNo");
    id(r.routeCode, "routeCode");
    id(r.scheduleNo, "scheduleNo");
    id(r.driverRef, "driverRef");
    id(r.conductorRef, "conductorRef");
    if (r.shift !== "I" && r.shift !== "II")
        e.push("shift must be I or II");
    if (!DATE_RE.test(r.date))
        e.push("date must be YYYY-MM-DD");
    nonNegInt(r.odometerOut, "odometerOut");
    nonNegInt(r.odometerIn, "odometerIn");
    if (isInt(r.odometerIn) && isInt(r.odometerOut) && r.odometerIn < r.odometerOut)
        e.push("odometerIn cannot be less than odometerOut");
    soc(r.socOut, "socOut");
    soc(r.socIn, "socIn");
    nonNegInt(r.breakTimeMin, "breakTimeMin");
    if (r.trips.length === 0)
        e.push("at least one trip is required");
    const seqs = new Set();
    r.trips.forEach((t, i) => {
        if (!isInt(t.seq) || t.seq < 1)
            e.push(`trip[${i}].seq must be an integer >= 1`);
        if (seqs.has(t.seq))
            e.push(`trip seq ${t.seq} is duplicated`);
        seqs.add(t.seq);
        if (!t.from.trim())
            e.push(`trip[${i}].from is required`);
        if (!t.to.trim())
            e.push(`trip[${i}].to is required`);
        if (!TIME_RE.test(t.arrivalTime))
            e.push(`trip[${i}].arrivalTime must be HH:MM`);
        if (!TIME_RE.test(t.departureTime))
            e.push(`trip[${i}].departureTime must be HH:MM`);
        soc(t.soc, `trip[${i}].soc`);
        nonNegInt(t.tripKm, `trip[${i}].tripKm`);
    });
    nonNegInt(r.scheduledKm, "scheduledKm");
    nonNegInt(r.actualKm, "actualKm");
    nonNegInt(r.lossKm, "lossKm");
    // Loss must reconcile: lossKm = max(scheduledKm − actualKm, 0), and the
    // entries must account for it, each with a reason code (§4.1.1-4).
    if (isInt(r.scheduledKm) && isInt(r.actualKm) && isInt(r.lossKm)) {
        const expectedLoss = Math.max(r.scheduledKm - r.actualKm, 0);
        if (r.lossKm !== expectedLoss)
            e.push(`lossKm must equal max(scheduledKm - actualKm, 0) = ${expectedLoss}`);
    }
    let lossSum = 0;
    r.lossEntries.forEach((le, i) => {
        if (!LOSS_REASON_CODES.includes(le.code))
            e.push(`lossEntries[${i}].code invalid`);
        nonNegInt(le.km, `lossEntries[${i}].km`);
        if (isInt(le.km))
            lossSum += le.km;
    });
    if (isInt(r.lossKm) && r.lossKm > 0 && lossSum !== r.lossKm)
        e.push(`lossEntries must sum to lossKm (${r.lossKm}), got ${lossSum}`);
    if (!TIME_RE.test(r.memoGivenTime) && r.memoGivenTime !== "")
        e.push("memoGivenTime must be HH:MM or empty");
    if (!TIME_RE.test(r.memoTakenTime) && r.memoTakenTime !== "")
        e.push("memoTakenTime must be HH:MM or empty");
    if (r.dutyStatus !== "OK_DUTY" && r.dutyStatus !== "NOT_OK_DUTY")
        e.push("dutyStatus must be OK_DUTY or NOT_OK_DUTY");
    if (Number.isNaN(Date.parse(r.captureTimestamp)))
        e.push("captureTimestamp must be an ISO-8601 timestamp");
    return e;
}
/** Casual-contract kilometres of a return (feeds Annexure 8.2 column E). */
export function casualContractKm(r) {
    return r.lossEntries
        .filter((l) => l.code === "CASUAL_CONTRACT")
        .reduce((s, l) => s + l.km, 0);
}
export function rollUpDailyKMStatement(r, mis) {
    for (const [k, v] of Object.entries(mis)) {
        if (!isInt(v) || v < 0)
            throw new Error(`MIS input ${k} must be a non-negative integer`);
    }
    return {
        artefactType: "DAILY_KM_STATEMENT",
        depotId: r.depotId,
        date: r.date,
        busRegistrationNo: r.busRegistrationNo,
        returnVersion: r.version,
        operationalKm: r.actualKm - casualContractKm(r),
        casualContractKm: casualContractKm(r),
        energyKWh: mis.energyKWh,
        fareboxRevenuePaise: mis.fareboxRevenuePaise,
        nonFareboxRevenuePaise: mis.nonFareboxRevenuePaise,
        breakdownCount: r.lossEntries.filter((l) => l.code === "BREAKDOWN").length,
    };
}
/** A blank return for the capture form, pre-configured per depot. */
export function emptyDailyReturn(depotId, operatorEntity) {
    return {
        artefactType: ARTEFACT_DAILY_RETURN,
        version: 1,
        depotId,
        operatorEntity,
        shift: "I",
        busRegistrationNo: "",
        routeCode: "",
        scheduleNo: "",
        date: "",
        odometerOut: 0,
        odometerIn: 0,
        socOut: 100,
        socIn: 0,
        breakTimeMin: 0,
        driverRef: "",
        conductorRef: "",
        trips: [],
        scheduledKm: 0,
        actualKm: 0,
        lossKm: 0,
        lossEntries: [],
        vehicleChecklist: {
            brakes: false, clutch: false, steering: false, coolant: false,
            wiper: false, battery: false, brakeOil: false, tyrePressure: false,
            engineCondition: false, bodyCondition: false,
            sideFront: false, sideRear: false, sideLeft: false, sideRight: false,
            remarks: "",
        },
        memoGivenTime: "",
        memoTakenTime: "",
        dutyStatus: "OK_DUTY",
        captureTimestamp: "",
    };
}
